import {
  addR,
  divR,
  equalsR,
  formatStyled,
  intR,
  isZeroR,
  mulR,
  type NumberStyle,
  negR,
  parseRational,
  RATIONAL_ZERO,
  type Rational,
  subR,
} from "@/lib/tool-kit/math/rational";

/**
 * Exact matrices over the rationals.
 *
 * Every entry is a reduced fraction on BigInt, so a determinant of 1/3 stays 1/3 and an
 * inverse is exact, never a float that is almost an integer. The row-reduction routines
 * record each elementary row operation with the matrix it produced, so the widget can
 * show the working the way a textbook does.
 */

export type Matrix = Rational[][];

export const R_ZERO = RATIONAL_ZERO;
export const R_ONE = intR(1);

export function rowCount(m: Matrix): number {
  return m.length;
}

export function colCount(m: Matrix): number {
  return m[0]?.length ?? 0;
}

export function identity(n: number): Matrix {
  return Array.from({ length: n }, (_, i) =>
    Array.from({ length: n }, (_, j) => (i === j ? R_ONE : R_ZERO))
  );
}

export function zeros(rows: number, cols: number): Matrix {
  return Array.from({ length: rows }, () =>
    Array.from({ length: cols }, () => R_ZERO)
  );
}

export function copyMatrix(m: Matrix): Matrix {
  return m.map((row) => row.slice());
}

export type ParseResult =
  | { ok: true; matrix: Matrix }
  | { ok: false; reason: "empty" | "invalid" };

/** Read a grid of typed cells (integers, decimals, fractions) into an exact matrix. */
export function parseMatrix(
  cells: readonly (readonly string[])[]
): ParseResult {
  const matrix: Matrix = [];
  let sawEmpty = false;
  for (const row of cells) {
    const parsed: Rational[] = [];
    for (const cell of row) {
      if (cell.trim() === "") {
        sawEmpty = true;
        parsed.push(R_ZERO);
        continue;
      }
      const value = parseRational(cell);
      if (value === null) {
        return { ok: false, reason: "invalid" };
      }
      parsed.push(value);
    }
    matrix.push(parsed);
  }
  return sawEmpty ? { ok: false, reason: "empty" } : { ok: true, matrix };
}

/* ------------------------------------------------------------ arithmetic */

export function addMatrix(a: Matrix, b: Matrix): Matrix {
  return a.map((row, i) => row.map((value, j) => addR(value, b[i][j])));
}

export function subtractMatrix(a: Matrix, b: Matrix): Matrix {
  return a.map((row, i) => row.map((value, j) => subR(value, b[i][j])));
}

export function scaleMatrix(a: Matrix, k: Rational): Matrix {
  return a.map((row) => row.map((value) => mulR(value, k)));
}

export function transpose(a: Matrix): Matrix {
  return Array.from({ length: colCount(a) }, (_, j) => a.map((row) => row[j]));
}

export function dotRows(row: readonly Rational[], column: readonly Rational[]) {
  let sum = R_ZERO;
  for (let k = 0; k < row.length; k += 1) {
    sum = addR(sum, mulR(row[k], column[k]));
  }
  return sum;
}

export function multiplyMatrix(a: Matrix, b: Matrix): Matrix {
  const columns = transpose(b);
  return a.map((row) => columns.map((column) => dotRows(row, column)));
}

export function matrixPower(a: Matrix, exponent: number): Matrix {
  let result = identity(a.length);
  let base = a;
  let remaining = exponent;
  while (remaining > 0) {
    if (remaining % 2 === 1) {
      result = multiplyMatrix(result, base);
    }
    remaining = Math.floor(remaining / 2);
    if (remaining > 0) {
      base = multiplyMatrix(base, base);
    }
  }
  return result;
}

export function trace(a: Matrix): Rational {
  let sum = R_ZERO;
  for (let i = 0; i < a.length; i += 1) {
    sum = addR(sum, a[i][i]);
  }
  return sum;
}

export function matricesEqual(a: Matrix, b: Matrix): boolean {
  return a.every((row, i) => row.every((value, j) => equalsR(value, b[i][j])));
}

/* ----------------------------------------------------------- row reduction */

export type RowOp =
  | { kind: "swap"; a: number; b: number }
  /** R_row → factor × R_row */
  | { kind: "scale"; row: number; factor: Rational }
  /** R_row → R_row + factor × R_source */
  | { kind: "add"; row: number; source: number; factor: Rational };

export interface ReductionStep {
  op: RowOp;
  /** The matrix after the operation. */
  matrix: Matrix;
  /** The pivot the operation belongs to (row and column, zero-based). */
  pivot: { row: number; col: number };
}

export interface Reduction {
  start: Matrix;
  steps: ReductionStep[];
  result: Matrix;
  /** Zero-based columns that hold a pivot, in order. */
  pivotCols: number[];
  swaps: number;
}

function firstNonzeroRow(m: Matrix, from: number, col: number): number {
  for (let r = from; r < m.length; r += 1) {
    if (!isZeroR(m[r][col])) {
      return r;
    }
  }
  return -1;
}

function eliminateOthers(
  work: Matrix,
  pivotRow: number,
  col: number,
  steps: ReductionStep[]
): void {
  for (let k = 0; k < work.length; k += 1) {
    if (k === pivotRow || isZeroR(work[k][col])) {
      continue;
    }
    const factor = negR(work[k][col]);
    work[k] = work[k].map((value, j) =>
      addR(value, mulR(factor, work[pivotRow][j]))
    );
    steps.push({
      op: { kind: "add", row: k, source: pivotRow, factor },
      matrix: copyMatrix(work),
      pivot: { row: pivotRow, col },
    });
  }
}

/**
 * Gauss–Jordan elimination to reduced row echelon form.
 *
 * Only the first `pivotColumns` columns are searched for pivots, which is how an
 * augmented matrix [A | I] is reduced without the identity half ever supplying one.
 */
export function reduceToRref(m: Matrix, pivotColumns = colCount(m)): Reduction {
  const work = copyMatrix(m);
  const steps: ReductionStep[] = [];
  const pivotCols: number[] = [];
  let swaps = 0;
  let pivotRow = 0;
  for (let col = 0; col < pivotColumns && pivotRow < work.length; col += 1) {
    const found = firstNonzeroRow(work, pivotRow, col);
    if (found === -1) {
      continue;
    }
    if (found !== pivotRow) {
      [work[found], work[pivotRow]] = [work[pivotRow], work[found]];
      swaps += 1;
      steps.push({
        op: { kind: "swap", a: pivotRow, b: found },
        matrix: copyMatrix(work),
        pivot: { row: pivotRow, col },
      });
    }
    const pivot = work[pivotRow][col];
    if (!equalsR(pivot, R_ONE)) {
      const factor = divR(R_ONE, pivot);
      work[pivotRow] = work[pivotRow].map((value) => mulR(value, factor));
      steps.push({
        op: { kind: "scale", row: pivotRow, factor },
        matrix: copyMatrix(work),
        pivot: { row: pivotRow, col },
      });
    }
    eliminateOthers(work, pivotRow, col, steps);
    pivotCols.push(col);
    pivotRow += 1;
  }
  return { start: copyMatrix(m), steps, result: work, pivotCols, swaps };
}

export function rank(m: Matrix): number {
  return reduceToRref(m).pivotCols.length;
}

/* ---------------------------------------------------------------- inverse */

export interface InverseResult {
  augmented: Matrix;
  reduction: Reduction;
  invertible: boolean;
  inverse: Matrix | null;
}

/** Invert a square matrix by reducing [A | I]; the right half becomes A⁻¹. */
export function invertMatrix(a: Matrix): InverseResult {
  const n = a.length;
  const eye = identity(n);
  const augmented = a.map((row, i) => [...row, ...eye[i]]);
  const reduction = reduceToRref(augmented, n);
  const invertible = reduction.pivotCols.length === n;
  return {
    augmented,
    reduction,
    invertible,
    inverse: invertible ? reduction.result.map((row) => row.slice(n)) : null,
  };
}

/* ------------------------------------------------------------ determinant */

export function minorMatrix(m: Matrix, row: number, col: number): Matrix {
  return m
    .filter((_, i) => i !== row)
    .map((r) => r.filter((_, j) => j !== col));
}

export function det2(m: Matrix): Rational {
  return subR(mulR(m[0][0], m[1][1]), mulR(m[0][1], m[1][0]));
}

export interface CofactorTerm {
  col: number;
  entry: Rational;
  /** +1 or −1: (−1)^(1 + col) with one-based columns. */
  sign: 1 | -1;
  minor: Matrix;
  minorDet: Rational;
  /** sign × entry × minorDet */
  contribution: Rational;
}

export interface Cofactor {
  terms: CofactorTerm[];
  value: Rational;
}

/** Determinant by cofactor expansion along the first row (sizes 2 and 3 only). */
export function cofactorExpansion(m: Matrix): Cofactor {
  const terms: CofactorTerm[] = [];
  let value = R_ZERO;
  for (let col = 0; col < m.length; col += 1) {
    const minor = minorMatrix(m, 0, col);
    const minorDet = minor.length === 1 ? minor[0][0] : det2(minor);
    const sign = col % 2 === 0 ? 1 : -1;
    const entry = m[0][col];
    const contribution = mulR(mulR(entry, minorDet), intR(sign));
    terms.push({ col, entry, sign, minor, minorDet, contribution });
    value = addR(value, contribution);
  }
  return { terms, value };
}

export interface Elimination {
  start: Matrix;
  steps: ReductionStep[];
  triangular: Matrix;
  swaps: number;
  determinant: Rational;
}

/** Forward elimination to upper-triangular form; swaps flip the sign, adds change nothing. */
export function eliminateForDeterminant(m: Matrix): Elimination {
  const work = copyMatrix(m);
  const steps: ReductionStep[] = [];
  let swaps = 0;
  let zeroColumn = false;
  for (let col = 0; col < work.length; col += 1) {
    const found = firstNonzeroRow(work, col, col);
    if (found === -1) {
      zeroColumn = true;
      continue;
    }
    if (found !== col) {
      [work[found], work[col]] = [work[col], work[found]];
      swaps += 1;
      steps.push({
        op: { kind: "swap", a: col, b: found },
        matrix: copyMatrix(work),
        pivot: { row: col, col },
      });
    }
    for (let k = col + 1; k < work.length; k += 1) {
      if (isZeroR(work[k][col])) {
        continue;
      }
      const factor = negR(divR(work[k][col], work[col][col]));
      work[k] = work[k].map((value, j) =>
        addR(value, mulR(factor, work[col][j]))
      );
      steps.push({
        op: { kind: "add", row: k, source: col, factor },
        matrix: copyMatrix(work),
        pivot: { row: col, col },
      });
    }
  }
  let determinant = swaps % 2 === 0 ? R_ONE : intR(-1);
  for (let i = 0; i < work.length; i += 1) {
    determinant = mulR(determinant, work[i][i]);
  }
  return {
    start: copyMatrix(m),
    steps,
    triangular: work,
    swaps,
    determinant: zeroColumn ? R_ZERO : determinant,
  };
}

export function determinant(m: Matrix): Rational {
  if (m.length === 1) {
    return m[0][0];
  }
  if (m.length === 2) {
    return det2(m);
  }
  return eliminateForDeterminant(m).determinant;
}

/* ---------------------------------------------------- null space, polynomial */

/** A basis of {x : A x = 0}, from the RREF: one vector per free column. */
export function nullSpace(a: Matrix): Rational[][] {
  const cols = colCount(a);
  const { result, pivotCols } = reduceToRref(a);
  const free: number[] = [];
  for (let c = 0; c < cols; c += 1) {
    if (!pivotCols.includes(c)) {
      free.push(c);
    }
  }
  return free.map((freeCol) => {
    const vector = Array.from({ length: cols }, () => R_ZERO);
    vector[freeCol] = R_ONE;
    pivotCols.forEach((pivotCol, row) => {
      vector[pivotCol] = negR(result[row][freeCol]);
    });
    return vector;
  });
}

/**
 * Coefficients of det(λI − A), lowest power first, by the Faddeev–LeVerrier recurrence.
 * Exact because every step is rational arithmetic; the result is monic.
 */
export function characteristicPolynomial(a: Matrix): Rational[] {
  const n = a.length;
  const coefficients: Rational[] = Array.from({ length: n + 1 }, () => R_ZERO);
  coefficients[n] = R_ONE;
  let m = zeros(n, n);
  for (let k = 1; k <= n; k += 1) {
    const shifted = multiplyMatrix(a, m);
    m = shifted.map((row, i) =>
      row.map((value, j) =>
        i === j ? addR(value, coefficients[n - k + 1]) : value
      )
    );
    coefficients[n - k] = negR(divR(trace(multiplyMatrix(a, m)), intR(k)));
  }
  return coefficients;
}

/* ------------------------------------------------------------- formatting */

export function formatMatrixText(
  m: Matrix,
  style: NumberStyle,
  decimalMark: string
): string {
  return m
    .map((row) =>
      row.map((v) => formatStyled(v, style, decimalMark)).join("\t")
    )
    .join("\n");
}
